import {
  PathsV1BranchSearchPostParametersQueryExtend,
  PathsV1ModelGenerationSearchPostParametersQueryExtend,
  PathsV1VehicleSearchPostParametersQuerySearchField,
  PathsV1VehicleSearchPostParametersQuerySelect,
  ServiceHistoryStatus,
  VehicleAccidentState,
  VehicleDrivetrain,
  VehicleUsage,
} from "@/api-schema-enums";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { useModelGenerationSearchQuery } from "@/common/api/model-generation/use-model-generation-search.query";
import {
  useVehicleExchangeEngineCreateMutation,
  useVehicleExchangeEngineDeleteMutation,
  useVehicleExchangeEngineUpdateMutation,
} from "@/common/api/vehicle-exchange-engine/use-vehicle-exchange-engine.mutations";
import { useVehicleCreateMutation } from "@/common/api/vehicle/use-vehicle-create.mutation";
import { useVehicleSearchQuery } from "@/common/api/vehicle/use-vehicle-search.query";
import { useVehicleUpdateMutation } from "@/common/api/vehicle/use-vehicle-update.mutation";
import { VehicleWithRegistrationDate } from "@/common/api/vehicle/vehicle.types";
import { inputStringToDate } from "@/common/helpers/date-input";
import { formatDateToApi } from "@/common/helpers/format-date";
import { createPlaceholderKeys } from "@/common/helpers/list-keys";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { VEHICLE_COLOR_LABELS, VEHICLE_KIND_LABELS } from "@/common/helpers/vehicle-type-labels";
import { getVehicleVinValidation } from "@/common/helpers/vehicle-vin";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { VehicleVinFieldStatus } from "@/components/vehicle-vin/vehicle-vin-field-status";
import { getVehicleValuationRimTypeOptions } from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-metadata";
import { SystemInfoFields } from "@/components/system-info-fields/system-info-fields";
import { getBranchV1Identifier } from "@/modules/masterdata/branch/branch-identifiers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import {
  POSTGRES_INTEGER_MAX,
  VEHICLE_COLOR_VALUES,
  VEHICLE_KIND_VALUES,
  VehicleFormSchema,
  type VehicleFormMode,
  type VehicleFormSchemaType,
} from "../_helpers/vehicle-form-schema";
import {
  getVehicleBranchPayload,
  getVehicleFormPayload,
  getVehicleFormValues,
  toModelGenerationComboboxOption,
} from "../_helpers/vehicle-form.helpers";

const DUPLICATE_VALIDATION_ERROR_DURATION = 5000;
const COMBOBOX_PAGE_SIZE = 50;
const MODEL_GENERATION_MAX_PAGE_SIZE = 1000;

interface VehicleFormFieldsProps {
  mode: VehicleFormMode;
  uuid?: string;
  vehicle?: VehicleWithRegistrationDate;
}

type VehicleSummary = {
  uuid: string;
  vin: string;
};

export function VehicleFormFields({ mode, uuid, vehicle }: VehicleFormFieldsProps) {
  const { t, i18n } = useTranslation(["masterdata", "common", "fit", "fab"]);
  const [_, navigate] = useLocation();
  const isUpdateMode = mode === "update";

  const isFormReady = mode === "create" || !!vehicle;

  const createMutation = useVehicleCreateMutation();
  const updateMutation = useVehicleUpdateMutation();
  const createExchangeEngineMutation = useVehicleExchangeEngineCreateMutation();
  const updateExchangeEngineMutation = useVehicleExchangeEngineUpdateMutation();
  const deleteExchangeEngineMutation = useVehicleExchangeEngineDeleteMutation();

  const modelGenerationComboboxState = useComboboxPagination({
    initialPageSize: COMBOBOX_PAGE_SIZE,
    maxPageSize: MODEL_GENERATION_MAX_PAGE_SIZE,
  });
  const branchComboboxState = useComboboxPagination({ initialPageSize: COMBOBOX_PAGE_SIZE });
  const vehicleFormValues = useMemo(
    () => (isUpdateMode && vehicle ? getVehicleFormValues(vehicle) : undefined),
    [isUpdateMode, vehicle],
  );

  const form = useForm<VehicleFormSchemaType>({
    resolver: zodResolver(VehicleFormSchema(t, mode)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: vehicleFormValues ?? {
      modelGeneration: "",
      modelDescription: "",
      vin: "",
      licensePlate: "",
      manufacturerColor: "",
      color: "",
      registrationDate: "",
      huDate: "",
      serviceHistory: "",
      vehicleUsage: "",
      rimType: "",
      exchangeEngineInstalled: false,
      exchangeDate: "",
      engineMileageAtExchange: "",
      vehicleMileageAtExchange: "",
      exchangeNotes: "",
      drivetrain: "",
      vehicleKind: "",
      accidentState: "",
      accidentDescription: "",
      accidentValue: "",
      vatDeductible: "",
      reimport: "false",
      engineKilowatts: "",
      engineDisplacementCcm: "",
      numberDoors: "",
      mileage: "",
      owningBranch: "",
      currentBranch: "",
    },
  });
  const { isSubmitted, isValid, touchedFields } = form.formState;
  const showRegistrationDateError = !!touchedFields.registrationDate || isSubmitted;
  const showHuDateError = !!touchedFields.huDate || isSubmitted;

  const [vinValue, owningBranchValue, currentBranchValue] = useWatch({
    control: form.control,
    name: ["vin", "owningBranch", "currentBranch"],
  });
  const exchangeEngineInstalled = useWatch({ control: form.control, name: "exchangeEngineInstalled" });
  const vinLengthDone = !!touchedFields.vin || isSubmitted;
  const vinValidation = getVehicleVinValidation(vinValue ?? "", vinLengthDone);
  const normalizedVinValue = vinValue?.trim().toUpperCase() ?? "";
  const shouldCheckDuplicateVin = normalizedVinValue.length === 17;

  const { data: modelGenerations, isPending: isModelGenerationsLoading } = useModelGenerationSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: modelGenerationComboboxState.pageSize,
        search: modelGenerationComboboxState.debouncedSearchTerm || undefined,
        searchField: ["name", "modelFamily.name", "modelFamily.brand.name"],
        extend: [
          PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelFamily,
          PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelFamily_Brand,
        ],
      },
    },
  });

  const { data: branches, isPending: isBranchesLoading } = useBranchSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: branchComboboxState.pageSize,
        search: branchComboboxState.debouncedSearchTerm || undefined,
        extend: [
          PathsV1BranchSearchPostParametersQueryExtend.Company,
          PathsV1BranchSearchPostParametersQueryExtend.Brands,
          PathsV1BranchSearchPostParametersQueryExtend.Address,
          PathsV1BranchSearchPostParametersQueryExtend.BranchIdentifiers,
        ],
      },
    },
  });

  const { data: matchingVehicles, isFetching: isDuplicateVinCheckLoading } = useVehicleSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: 10,
        search: normalizedVinValue,
        searchField: [PathsV1VehicleSearchPostParametersQuerySearchField.Vin],
        select: [PathsV1VehicleSearchPostParametersQuerySelect.Uuid, PathsV1VehicleSearchPostParametersQuerySelect.Vin],
      },
    },
    enabled: shouldCheckDuplicateVin,
  });

  useEffect(() => {
    if (!owningBranchValue || currentBranchValue) {
      return;
    }

    form.setValue("currentBranch", owningBranchValue, { shouldDirty: true, shouldValidate: true });
  }, [currentBranchValue, form, owningBranchValue]);

  const mappedModelGenerations = useMemo<ComboboxOption[]>(() => {
    const options = (modelGenerations?.data ?? []).map((modelGeneration) =>
      toModelGenerationComboboxOption(modelGeneration, i18n.language),
    );
    const selectedModelGeneration = vehicle?.modelGeneration;

    if (
      !modelGenerationComboboxState.debouncedSearchTerm &&
      selectedModelGeneration &&
      !options.some((option) => option.value === selectedModelGeneration.uuid)
    ) {
      options.push(toModelGenerationComboboxOption(selectedModelGeneration, i18n.language));
    }

    return options
      .filter((option) => option.value && option.label)
      .sort(
        (leftOption, rightOption) =>
          leftOption.label.localeCompare(rightOption.label) ||
          (leftOption.description ?? "").localeCompare(rightOption.description ?? ""),
      );
  }, [
    i18n.language,
    modelGenerationComboboxState.debouncedSearchTerm,
    modelGenerations?.data,
    vehicle?.modelGeneration,
  ]);

  const mappedBranches = useMemo<ComboboxOption[]>(() => {
    return (
      branches?.data
        ?.map((branch) => {
          const label = branch.name;
          const searchParts = [branch.name, branch.company?.name, branch.address?.city, getBranchV1Identifier(branch)]
            .filter(Boolean)
            .map((part) => String(part));

          return {
            value: branch.uuid ?? "",
            label,
            searchText: searchParts.join(" ").toLowerCase(),
          };
        })
        .filter((option) => option.value && option.label)
        .sort((leftOption, rightOption) => leftOption.label.localeCompare(rightOption.label)) ?? []
    );
  }, [branches]);

  const duplicateValidation = useMemo(() => {
    if (!shouldCheckDuplicateVin || !matchingVehicles?.data) {
      return {
        hasDuplicateVin: false,
        duplicateVinMessage: null as string | null,
      };
    }

    const duplicateVehicle = matchingVehicles.data.find((existingVehicle) => {
      const vehicleSummary = existingVehicle as VehicleSummary;
      return vehicleSummary.vin?.trim().toUpperCase() === normalizedVinValue && vehicleSummary.uuid !== uuid;
    });

    if (!duplicateVehicle) {
      return {
        hasDuplicateVin: false,
        duplicateVinMessage: null as string | null,
      };
    }

    return {
      hasDuplicateVin: true,
      duplicateVinMessage: t("masterdata:VehicleVinAlreadyExists", { vin: normalizedVinValue }),
    };
  }, [matchingVehicles?.data, normalizedVinValue, shouldCheckDuplicateVin, t, uuid]);

  const modelGenerationPagination = modelGenerationComboboxState.createPaginationState(
    modelGenerations?.meta?.total ?? 0,
    modelGenerations?.data?.length ?? 0,
    isModelGenerationsLoading,
  );

  const branchPagination = branchComboboxState.createPaginationState(
    branches?.meta?.total ?? 0,
    branches?.data?.length ?? 0,
    isBranchesLoading,
  );

  const accidentStateOptions = useMemo(
    () => [
      { value: VehicleAccidentState.UNKNOWN, label: t("masterdata:VehicleAccidentStateUnknown") },
      { value: VehicleAccidentState.AccidentFree, label: t("masterdata:VehicleAccidentStateAccidentFree") },
      { value: VehicleAccidentState.ExistingDamage, label: t("masterdata:VehicleAccidentStateExistingDamage") },
      { value: VehicleAccidentState.RepairedDamage, label: t("masterdata:VehicleAccidentStateRepairedDamage") },
    ],
    [t],
  );

  const drivetrainOptions = useMemo(
    () => [
      { value: VehicleDrivetrain.UNKNOWN, label: t("masterdata:VehicleDrivetrainUnknown") },
      { value: VehicleDrivetrain.FrontWheelDrive, label: t("masterdata:VehicleDrivetrainFront") },
      { value: VehicleDrivetrain.RearWheelDrive, label: t("masterdata:VehicleDrivetrainRear") },
      { value: VehicleDrivetrain.FourWheelDrive, label: t("masterdata:VehicleDrivetrainFourWheelDrive") },
    ],
    [t],
  );

  const serviceHistoryOptions = useMemo(
    () => [
      { value: ServiceHistoryStatus.UNKNOWN, label: t("masterdata:ServiceHistoryUnknown") },
      { value: ServiceHistoryStatus.NONE, label: t("masterdata:ServiceHistoryNone") },
      { value: ServiceHistoryStatus.PARTIAL, label: t("masterdata:ServiceHistoryPartial") },
      { value: ServiceHistoryStatus.FullServiceHistory, label: t("masterdata:ServiceHistoryFull") },
      { value: ServiceHistoryStatus.FullDealerServiceHistory, label: t("masterdata:ServiceHistoryFullDealer") },
    ],
    [t],
  );

  const vehicleUsageOptions = useMemo(
    () => [
      { value: VehicleUsage.UNKNOWN, label: t("masterdata:VehicleUsageUnknown") },
      { value: VehicleUsage.RegularPersonalCommercial, label: t("masterdata:VehicleUsageRegular") },
      { value: VehicleUsage.RENTAL, label: t("masterdata:VehicleUsageRental") },
      { value: VehicleUsage.TaxiRideHailing, label: t("masterdata:VehicleUsageTaxi") },
      { value: VehicleUsage.DrivingSchool, label: t("masterdata:VehicleUsageDrivingSchool") },
      { value: VehicleUsage.DEMO, label: t("masterdata:VehicleUsageDemo") },
      { value: VehicleUsage.FleetPool, label: t("masterdata:VehicleUsageFleetPool") },
      { value: VehicleUsage.DeliveryCourier, label: t("masterdata:VehicleUsageDelivery") },
      { value: VehicleUsage.SpecialPurpose, label: t("masterdata:VehicleUsageSpecialPurpose") },
      { value: VehicleUsage.CONSTRUCTION, label: t("masterdata:VehicleUsageConstruction") },
      { value: VehicleUsage.ManagementCompanyCar, label: t("masterdata:VehicleUsageManagementCompanyCar") },
      { value: VehicleUsage.EmployeeCompanyCar, label: t("masterdata:VehicleUsageEmployeeCompanyCar") },
    ],
    [t],
  );
  const rimTypeOptions = useMemo(() => getVehicleValuationRimTypeOptions(t), [t]);

  const vehicleKindOptions = useMemo(() => {
    const lang = i18n.language === "de" ? "de" : "en";

    return VEHICLE_KIND_VALUES.map((value) => ({
      value,
      label: VEHICLE_KIND_LABELS[value]?.[lang] ?? value,
    }));
  }, [i18n.language]);

  const vehicleColorOptions = useMemo(() => {
    const lang = i18n.language === "de" ? "de" : "en";

    return VEHICLE_COLOR_VALUES.map((value) => ({
      value,
      label: VEHICLE_COLOR_LABELS[value]?.[lang] ?? value,
    }));
  }, [i18n.language]);

  const booleanOptions = useMemo(
    () => [
      { value: "true", label: t("common:Yes") },
      { value: "false", label: t("common:No") },
    ],
    [t],
  );
  const vatDeductibleOptions = useMemo(
    () => [
      { value: "true", label: t("masterdata:VatDeductibleYesOption") },
      { value: "false", label: t("masterdata:VatDeductibleNoOption") },
    ],
    [t],
  );

  const handleSubmitWithValidation = async (values: VehicleFormSchemaType) => {
    if (duplicateValidation.hasDuplicateVin && duplicateValidation.duplicateVinMessage) {
      toast.error(t("common:ValidationError"), {
        description: duplicateValidation.duplicateVinMessage,
        duration: DUPLICATE_VALIDATION_ERROR_DURATION,
      });
      return;
    }

    const exchangeEngineBody = values.exchangeEngineInstalled
      ? {
          exchangeDate: formatDateToApi(inputStringToDate(values.exchangeDate)!),
          engineMileageAtExchange: values.engineMileageAtExchange
            ? Number.parseInt(values.engineMileageAtExchange, 10)
            : null,
          vehicleMileageAtExchange: Number.parseInt(values.vehicleMileageAtExchange, 10),
          notes: values.exchangeNotes.trim() || null,
        }
      : undefined;

    const payload = {
      ...getVehicleFormPayload(values),
      ...(!isUpdateMode && exchangeEngineBody ? { exchangeEngine: exchangeEngineBody } : {}),
    };

    const syncExchangeEngine = async (vehicleUuid: string) => {
      const existingExchangeEngine = vehicle?.exchangeEngine;

      if (!values.exchangeEngineInstalled) {
        if (existingExchangeEngine?.uuid) {
          await deleteExchangeEngineMutation.mutateAsync({ uuid: existingExchangeEngine.uuid });
        }
        return;
      }

      if (!exchangeEngineBody) {
        return;
      }

      if (existingExchangeEngine?.uuid) {
        await updateExchangeEngineMutation.mutateAsync({
          uuid: existingExchangeEngine.uuid,
          ...exchangeEngineBody,
        });
        return;
      }

      await createExchangeEngineMutation.mutateAsync({ vehicle: vehicleUuid, ...exchangeEngineBody });
    };

    try {
      await toastMutationStatus({
        mutationFn: async () => {
          if (isUpdateMode) {
            const savedVehicle = await updateMutation.mutateAsync({
              uuid: uuid ?? "",
              ...payload,
              ...getVehicleBranchPayload(values, "update"),
            });
            await syncExchangeEngine(savedVehicle.uuid);
            return savedVehicle;
          }

          return createMutation.mutateAsync({
            ...payload,
            ...getVehicleBranchPayload(values, "create"),
          });
        },
        successMessage: isUpdateMode
          ? t("masterdata:VehicleSuccessfullyUpdated")
          : t("masterdata:VehicleSuccessfullyCreated"),
        errorMessage: isUpdateMode ? t("masterdata:ErrorUpdatingVehicle") : t("masterdata:ErrorCreatingVehicle"),
        onSuccess: () => navigate(isUpdateMode && uuid ? `~/masterdata/vehicle/${uuid}` : "~/masterdata/vehicle"),
      });
    } catch (error) {
      console.error(`Error ${isUpdateMode ? "updating" : "creating"} vehicle:`, error);
    }
  };

  const isSubmitDisabled =
    createMutation.isPending ||
    updateMutation.isPending ||
    createExchangeEngineMutation.isPending ||
    updateExchangeEngineMutation.isPending ||
    deleteExchangeEngineMutation.isPending ||
    duplicateValidation.hasDuplicateVin ||
    !isValid;

  if (!isFormReady) {
    return (
      <div className="space-y-6">
        {createPlaceholderKeys(6, "skeleton").map((key) => (
          <div key={key} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ))}

        <div className="flex justify-end space-x-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="modelGeneration"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("masterdata:ModelGeneration")} *</FormLabel>
                  <Combobox
                    options={mappedModelGenerations}
                    value={field.value}
                    onChange={field.onChange}
                    isLoading={modelGenerationPagination.isLoading}
                    onSearchChange={modelGenerationPagination.onSearchChange}
                    shouldFilter={false}
                    hasMore={modelGenerationPagination.hasMore}
                    remainingCount={modelGenerationPagination.remainingCount}
                    isLoadingMore={modelGenerationPagination.isLoadingMore}
                    onLoadMore={modelGenerationPagination.onLoadMore}
                    placeholder={t("common:SelectModel")}
                    searchPlaceholder={t("common:SearchModels")}
                    emptyText={t("common:NoModelsFound")}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="modelDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:ModelDescription")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder={t("masterdata:ModelDescriptionPlaceholder")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel showError={vinValidation.hasError}>{t("fit:VIN")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        maxLength={17}
                        placeholder={t("masterdata:VehicleVinPlaceholder")}
                        error={duplicateValidation.hasDuplicateVin || vinValidation.hasError}
                        onChange={(event) => {
                          field.onChange(event.target.value.toUpperCase());
                        }}
                      />
                      {isDuplicateVinCheckLoading && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <VehicleVinFieldStatus value={vinValue ?? ""} showLengthError={vinLengthDone} />
                  {duplicateValidation.hasDuplicateVin && duplicateValidation.duplicateVinMessage && (
                    <p className="text-error mt-1 text-sm">{t("masterdata:VehicleVinDuplicateError")}</p>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="licensePlate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:LicensePlate")}</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="manufacturerColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:ManufacturerColor")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder={t("masterdata:VehicleManufacturerColorPlaceholder")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:Color")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectVehicleColor")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {vehicleColorOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="registrationDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel showError={showRegistrationDateError}>{t("common:RegistrationDate")}</FormLabel>
                  <FormControl showError={showRegistrationDateError}>
                    <DatePickerInput
                      ref={field.ref}
                      name={field.name}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        void form.trigger("registrationDate");
                      }}
                      futureDaysDisabled
                    />
                  </FormControl>
                  <FormMessage showError={showRegistrationDateError} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="huDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel showError={showHuDateError}>{t("masterdata:HuDate")}</FormLabel>
                  <FormControl showError={showHuDateError}>
                    <DatePickerInput
                      ref={field.ref}
                      name={field.name}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        void form.trigger("huDate");
                      }}
                      futureDaysDisabled
                    />
                  </FormControl>
                  <FormMessage showError={showHuDateError} />
                </FormItem>
              )}
            />

            <div className="grid gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="serviceHistory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:ServiceHistory")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectServiceHistory")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {serviceHistoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vehicleUsage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:VehicleUsage")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectVehicleUsage")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {vehicleUsageOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="engineKilowatts"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:EngineKilowattsWithUnit")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="number"
                        min={0}
                        max={POSTGRES_INTEGER_MAX}
                        step={1}
                        inputMode="numeric"
                        placeholder={t("masterdata:EngineKilowattsPlaceholder")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="engineDisplacementCcm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:EngineDisplacementCcmWithUnit")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="number"
                        min={0}
                        max={POSTGRES_INTEGER_MAX}
                        step={1}
                        inputMode="numeric"
                        placeholder={t("masterdata:EngineDisplacementCcmPlaceholder")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="numberDoors"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:NumberDoors")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="number"
                        min={0}
                        max={POSTGRES_INTEGER_MAX}
                        step={1}
                        inputMode="numeric"
                        placeholder={t("masterdata:NumberDoorsPlaceholder")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mileage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:Mileage")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="number"
                        min={0}
                        max={POSTGRES_INTEGER_MAX}
                        step={1}
                        inputMode="numeric"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="drivetrain"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:Drivetrain")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectDrivetrain")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {drivetrainOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vehicleKind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:VehicleKind")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectVehicleKind")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {vehicleKindOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accidentState"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:AccidentState")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectAccidentState")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accidentStateOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accidentValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:DamageAmount")}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} type="number" min={0} step="0.01" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rimType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("fab:RimType")}</FormLabel>
                    <FormControl>
                      <Combobox
                        options={rimTypeOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t("fab:SelectRimType")}
                        clearLabel={t("common:ClearSelection")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vatDeductible"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:VatDeductible")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectVatDeductible")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {vatDeductibleOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reimport"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:Reimport")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("masterdata:SelectReimport")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {booleanOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="accidentDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:DamageDescription")}</FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value ?? ""} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="border-border space-y-6 border-t pt-6">
              <FormField
                control={form.control}
                name="exchangeEngineInstalled"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-4 space-y-0">
                    <FormLabel>{t("masterdata:ExchangeEngine")}</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {exchangeEngineInstalled ? (
                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="exchangeDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel required>{t("masterdata:ExchangeDate")}</FormLabel>
                        <FormControl>
                          <DatePickerInput
                            ref={field.ref}
                            name={field.name}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            futureDaysDisabled
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vehicleMileageAtExchange"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>{t("masterdata:VehicleMileageAtExchange")}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min={0}
                            max={POSTGRES_INTEGER_MAX}
                            step={1}
                            inputMode="numeric"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="engineMileageAtExchange"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("masterdata:EngineMileageAtExchange")}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min={0}
                            max={POSTGRES_INTEGER_MAX}
                            step={1}
                            inputMode="numeric"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="exchangeNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("masterdata:ExchangeNotes")}</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}
            </div>

            <FormField
              control={form.control}
              name="owningBranch"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>
                    {t("common:OwningBranch")}
                    {!isUpdateMode && " *"}
                  </FormLabel>
                  <Combobox
                    options={mappedBranches}
                    value={field.value}
                    onChange={field.onChange}
                    isLoading={branchPagination.isLoading}
                    onSearchChange={branchPagination.onSearchChange}
                    shouldFilter={false}
                    hasMore={branchPagination.hasMore}
                    remainingCount={branchPagination.remainingCount}
                    isLoadingMore={branchPagination.isLoadingMore}
                    onLoadMore={branchPagination.onLoadMore}
                    placeholder={t("common:SelectOwningBranch")}
                    searchPlaceholder={t("common:SearchBranches")}
                    emptyText={t("common:NoBranchesFound")}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currentBranch"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("fit:CurrentBranch")}</FormLabel>
                  <Combobox
                    options={mappedBranches}
                    value={field.value ?? ""}
                    onChange={(value) => field.onChange(value ?? "")}
                    isLoading={branchPagination.isLoading}
                    onSearchChange={branchPagination.onSearchChange}
                    shouldFilter={false}
                    hasMore={branchPagination.hasMore}
                    remainingCount={branchPagination.remainingCount}
                    isLoadingMore={branchPagination.isLoadingMore}
                    onLoadMore={branchPagination.onLoadMore}
                    placeholder={t("common:SelectCurrentBranch")}
                    searchPlaceholder={t("common:SearchBranches")}
                    emptyText={t("common:NoBranchesFound")}
                    clearLabel={t("common:ClearSelection")}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {isUpdateMode && vehicle && (
              <SystemInfoFields uuid={vehicle.uuid} createdAt={vehicle.createdAt} updatedAt={vehicle.updatedAt} />
            )}
          </CardContent>
        </Card>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => window.history.back()}
          isPending={createMutation.isPending || updateMutation.isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={isUpdateMode ? t("common:SaveChanges") : t("common:Save")}
        />
      </form>
    </Form>
  );
}
